// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the timeline's arithmetic
//  ----------------------------------------------------------------------------
//  DAYS AND PIXELS, and nothing else. No DOM, no React, no storage, no reading
//  of what a project is. Two surfaces feed it — the Workshop's timeline view and
//  the database's — and both hand it day keys and get back numbers.
//
//  ── why this file exists ──────────────────────────────────────────────────
//  Every timeline this app has drawn did its arithmetic inside its own render
//  function. DbTimelineContent computes a min, a max, a span and a fraction
//  inline, and snaps by dividing by 86400000. That is why the database's
//  timeline never earned its name: none of it can be asked a question without a
//  browser, so none of it was ever tested, and the bugs that live in it are the
//  kind only arithmetic has.
//
//  ── x IS A PHYSICAL LEFT OFFSET ───────────────────────────────────────────
//  In CSS pixels, inside a box `axis.width` wide, measured from the left edge —
//  NOT from the inline start. Under `rtl` the engine mirrors the axis itself, so
//  the surface positions everything with plain `left` and never thinks about
//  direction twice. Handing a mirrored number to `inset-inline-start` would
//  mirror it again, which is a bug that looks like a rendering glitch and is
//  actually two rights making a wrong.
//
//  ── 86400000 IS NOT IN THIS FILE ──────────────────────────────────────────
//  A day is not a fixed number of milliseconds. Adding 86,400,000 across a
//  daylight-saving boundary lands an hour inside the day before, and a timeline
//  that quietly loses a day twice a year is worse than one that cannot draw. Every
//  step is `setDate` on a local midnight, which is what the database's calendar
//  learned the same way (dbAddDays).
//
//  ── the week opens on Saturday ────────────────────────────────────────────
//  Because every other week in this app does — the day strip, the calendar
//  header. A ruler whose weeks start on a different day from the calendar two
//  screens away is a ruler nobody can read against anything.
// ════════════════════════════════════════════════════════════════════════════

/** How a timeline is being looked at. Wider pixels-per-day is a closer look. */
type MdTimelineZoom = 'day' | 'week' | 'month' | 'quarter';

/** A window of days, and how wide it is drawn. Everything below takes one. */
interface MdTimelineAxis {
  from: string;
  to: string;
  days: number;
  zoom: MdTimelineZoom;
  ppd: number;
  /* The factor between the named zoom's own pixels-per-day and the ones this
     axis was drawn at — 1 unless a pinch has been at it. */
  stretch: number;
  width: number;
  rtl: boolean;
}

interface MdTimelineSpanValue { start: string | null; target: string | null }

(function (g: any) {
  'use strict';

  const ZOOMS: MdTimelineZoom[] = ['day', 'week', 'month', 'quarter'];

  /* A day's width at each zoom. Chosen so that the unit you are named after is
     legible whole: a week is 168px at week zoom, a month roughly 270px at month
     zoom, a quarter roughly 360px at quarter zoom. */
  const PPD: Record<string, number> = { day: 56, week: 24, month: 9, quarter: 4 };

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

  /* A day that survives being looked up in a calendar. The third statement of
     this rule in the codebase — md-project.ts and md-database.ts have their own
     — and deliberately not shared: these three files have no runtime in common,
     only the rule, and a require between them would be a load-order problem
     traded for a duplicated regex. */
  function realDay(value: unknown): value is string {
    if (typeof value !== 'string' || !DAY_RE.test(value)) return false;
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(5, 7));
    const day = Number(value.slice(8, 10));
    const probe = new Date(Date.UTC(year, month - 1, day));
    return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1
      && probe.getUTCDate() === day;
  }

  const pad2 = (n: number) => (n < 10 ? '0' + n : String(n));

  /** A day key as a LOCAL midnight — the only Date this file ever makes. */
  function dateOf(day: string): Date {
    return new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
  }

  function keyOf(date: Date): string {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  }

  /** N days later, through the calendar. Negative goes back. */
  function mdTimelineAddDays(day: unknown, count: number): string | null {
    if (!realDay(day)) return null;
    const date = dateOf(day);
    date.setDate(date.getDate() + Math.round(count));
    return keyOf(date);
  }

  /** How many days from `a` to `b`, signed. Rounded because the two midnights
      can be an hour apart across a daylight-saving boundary. */
  function mdTimelineDayDiff(a: unknown, b: unknown): number {
    if (!realDay(a) || !realDay(b)) return 0;
    return Math.round((dateOf(b).getTime() - dateOf(a).getTime()) / 86400000);
  }

  /** Saturday, as everywhere else in this app. */
  function mdTimelineIsWeekStart(day: unknown): boolean {
    return realDay(day) ? dateOf(day).getDay() === 6 : false;
  }

  function mdTimelineIsMonthStart(day: unknown): boolean {
    return realDay(day) ? day.slice(8, 10) === '01' : false;
  }

  /* ── the scale between the names ────────────────────────────────────────
     A pinch is not a step. The four names are the vocabulary — they are what a
     chip says, what a view remembers, what the ruler picks its tick density
     from — but the SCALE is a continuous factor on top of whichever name is
     current, so a hand pulling the days apart is answered by the days coming
     apart rather than by a jump to the next preset.

     The two limits are the world's edges: below 2px a day, a decade fits on a
     laptop and nothing on it can be read; above 96px, a single day is wider
     than the name column and a year is a week of scrolling. */
  const PPD_MIN = 2;
  const PPD_MAX = 96;

  const realScale = (value: unknown): number => (typeof value === 'number'
    && Number.isFinite(value) && value > 0 ? value : 1);

  function mdTimelinePixelsPerDay(zoom: unknown, stretch?: unknown): number {
    const base = PPD[String(zoom)] || PPD.month!;
    return Math.min(PPD_MAX, Math.max(PPD_MIN, base * realScale(stretch)));
  }

  /** A pinch, in one step: the scale it asks for, under the name nearest it.
      Nearest is measured in RATIOS rather than pixels — the distance from 9 to
      24 is the distance from 24 to 64 to a hand, which is what makes the
      hand-over land in the middle of the gesture instead of near one end. */
  function mdTimelineZoomPinch(zoom: unknown, stretch: unknown, factor: unknown):
  { zoom: MdTimelineZoom; stretch: number } {
    const held: MdTimelineZoom = ZOOMS.indexOf(zoom as MdTimelineZoom) === -1
      ? 'month' : zoom as MdTimelineZoom;
    const want = Math.min(PPD_MAX, Math.max(PPD_MIN,
      mdTimelinePixelsPerDay(held, stretch) * realScale(factor)));
    let name: MdTimelineZoom = held;
    let nearest = Infinity;
    for (const option of ZOOMS) {
      const apart = Math.abs(Math.log(PPD[option]! / want));
      if (apart < nearest) { nearest = apart; name = option; }
    }
    return { zoom: name, stretch: want / PPD[name]! };
  }

  /** The window, or null if either end is not a real day. A `to` before its
      `from` collapses to a single day rather than a negative width. */
  function mdTimelineAxis(options: any): MdTimelineAxis | null {
    const from = options && options.from;
    const to = options && options.to;
    if (!realDay(from) || !realDay(to)) return null;
    const zoom: MdTimelineZoom = ZOOMS.indexOf(options.zoom) === -1 ? 'month' : options.zoom;
    const days = Math.max(1, mdTimelineDayDiff(from, to) + 1);
    const ppd = mdTimelinePixelsPerDay(zoom, options.stretch);
    return { from, to: mdTimelineAddDays(from, days - 1)!, days, zoom, ppd,
      stretch: realScale(options.stretch),
      width: days * ppd, rtl: !!(options && options.rtl) };
  }

  /* ── x ↔ day ──────────────────────────────────────────────────────────────
     Both directions answer OUTSIDE the window as well as inside it: a project
     that began before the window still has to be drawn off to the left, and a
     clamp here would put it flush against the edge and lie about where it
     begins. Clamping is a separate question, asked separately. */

  /** The left edge of a day's own cell. */
  function mdTimelineX(axis: MdTimelineAxis | null, day: unknown): number {
    if (!axis || !realDay(day)) return 0;
    const index = mdTimelineDayDiff(axis.from, day);
    return axis.rtl ? axis.width - (index + 1) * axis.ppd : index * axis.ppd;
  }

  /** Which day a pixel is inside. */
  function mdTimelineDayAt(axis: MdTimelineAxis | null, x: number): string | null {
    if (!axis) return null;
    const index = axis.rtl
      ? Math.ceil((axis.width - x) / axis.ppd) - 1
      : Math.floor(x / axis.ppd);
    return mdTimelineAddDays(axis.from, index);
  }

  function mdTimelineClampDay(axis: MdTimelineAxis | null, day: unknown): string | null {
    if (!axis || !realDay(day)) return null;
    if (day < axis.from) return axis.from;
    if (day > axis.to) return axis.to;
    return day;
  }

  /* ── the ruler ────────────────────────────────────────────────────────── */

  /** The month band, clipped to the window at both ends. The widths always sum
      to exactly the axis width, so the band can be laid out with no gaps and no
      remainder to explain. */
  function mdTimelineMonths(axis: MdTimelineAxis | null): any[] {
    if (!axis) return [];
    /* A window inside one year does not print the year sixty times; one that
       crosses a boundary has to, or December and January read the same. */
    const spansYears = axis.from.slice(0, 4) !== axis.to.slice(0, 4);
    const out: any[] = [];
    let day: string | null = axis.from;
    while (day && day <= axis.to) {
      const first = day;
      const date = dateOf(first);
      const lastOfMonth = keyOf(new Date(date.getFullYear(), date.getMonth() + 1, 0));
      const last = lastOfMonth > axis.to ? axis.to : lastOfMonth;
      const days = mdTimelineDayDiff(first, last) + 1;
      const width = days * axis.ppd;
      const startX = mdTimelineX(axis, first);
      out.push({ key: first.slice(0, 7), days, width,
        x: axis.rtl ? startX - width + axis.ppd : startX,
        label: MONTHS[date.getMonth()] + (spansYears ? ` ${date.getFullYear()}` : '') });
      day = mdTimelineAddDays(last, 1);
    }
    return out;
  }

  /** The ticks under the band. Up close, one per day and the weeks stand out;
      from far away, one per week and the months stand out. */
  function mdTimelineTicks(axis: MdTimelineAxis | null): any[] {
    if (!axis) return [];
    const daily = axis.zoom === 'day' || axis.zoom === 'week';
    const out: any[] = [];
    let day: string | null = axis.from;
    while (day && day <= axis.to) {
      const weekStart = mdTimelineIsWeekStart(day);
      if (daily || weekStart) {
        out.push({ day, x: mdTimelineX(axis, day), label: String(Number(day.slice(8, 10))),
          major: daily ? weekStart : mdTimelineIsMonthStart(day) });
      }
      day = mdTimelineAddDays(day, 1);
    }
    return out;
  }

  /** Where today's line goes, and whether it goes anywhere at all. */
  function mdTimelineToday(axis: MdTimelineAxis | null, todayKey: unknown): any {
    if (!axis || !realDay(todayKey)) return { x: 0, width: 0, visible: false };
    return { x: mdTimelineX(axis, todayKey), width: axis.ppd,
      visible: todayKey >= axis.from && todayKey <= axis.to };
  }

  /* ── a project's own rectangle ────────────────────────────────────────── */

  /** The box for one span. BOTH ENDS ARE INSIDE IT — a project that starts and
      finishes on the same day is one day wide, not nothing wide, and a project
      with only a target is a single day at that target rather than a bar
      stretching back to the beginning of the window. */
  function mdTimelineSpan(axis: MdTimelineAxis | null, start: unknown, target: unknown): any {
    if (!axis) return null;
    const a = realDay(start) ? start : null;
    const b = realDay(target) ? target : null;
    if (!a && !b) return null;
    const first = a && b ? (a < b ? a : b) : (a || b)!;
    const last = a && b ? (a < b ? b : a) : (a || b)!;
    const days = mdTimelineDayDiff(first, last) + 1;
    const width = days * axis.ppd;
    const startX = mdTimelineX(axis, first);
    return { first, last, days, width,
      x: axis.rtl ? startX - width + axis.ppd : startX };
  }

  /** The whole span, moved. A project with only a target moves by its target,
      and does not sprout a start it never had. */
  function mdTimelineMove(span: any, days: number): MdTimelineSpanValue {
    const start = span && realDay(span.start) ? mdTimelineAddDays(span.start, days) : null;
    const target = span && realDay(span.target) ? mdTimelineAddDays(span.target, days) : null;
    return { start, target };
  }

  /** One edge, moved. NEVER PAST THE OTHER: mdProjectSetSpan refuses an inverted
      span outright (W-1), so a surface that let the pointer produce one would be
      a surface whose drags silently do nothing at the far end. It folds at the
      other edge instead, which is what every calendar does.

      Pulling a start out of a project that only had a target is how a date
      BECOMES a span — the gesture that draws one rather than a second control. */
  function mdTimelineResize(span: any, edge: string, day: unknown): MdTimelineSpanValue {
    const start = span && realDay(span.start) ? span.start : null;
    const target = span && realDay(span.target) ? span.target : null;
    if (!realDay(day)) return { start, target };
    if (edge === 'start') {
      return { start: target && day > target ? target : day, target };
    }
    return { start, target: start && day < start ? start : day };
  }

  /* ── the window a set of rows needs ───────────────────────────────────── */

  const PAD_DAYS = 7;
  const MIN_DAYS = 28;
  /* TWO YEARS OF FUTURE, ALWAYS. A window that ends a week after the last date
     anybody has already set is a window in which no LATER date can be chosen:
     the day you want to drag to is not on the canvas, and no amount of
     scrolling brings it there. Planning is the point of a timeline, so the
     future is open by default and the past reaches back to the first thing
     that ever had a date. */
  const AHEAD_DAYS = 730;
  /* A LEAD-IN WIDE ENOUGH FOR A NAME COLUMN. The surfaces run the days behind
     their frozen column of names, so a window that begins a week before its
     earliest project begins it UNDERNEATH that column: the first bar opens
     hidden, and there is nothing further left to scroll to. This is the widest
     column a surface may have, in pixels, which is why it is stated here where
     the pixels are and converted at whatever zoom is asked for. */
  const LEAD_PX = 420;

  /** Wide enough to hold everything, with room either side to drag into, and
      today always in it — a timeline opening on a window that does not contain
      the present is a timeline whose first act is to make you scroll.

      `minDays` is the room the caller's viewport needs: a window narrower than
      the screen ends mid-air, and the rest is dead space nothing can be dragged
      into.

      `reach` is how much FURTHER than the default a surface has grown, in days:
      `{ behind, ahead }`. A scroller that reaches its own edge asks for more
      window rather than stopping, and this is where the extra is added — the
      surface keeps the count, the arithmetic stays here. */
  function mdTimelineWindow(rows: any[], todayKey: unknown, zoom?: unknown,
    minDays?: unknown, reach?: any, stretch?: unknown): any {
    const today = realDay(todayKey) ? todayKey : keyOf(new Date());
    let first = today;
    let last = today;
    for (const row of Array.isArray(rows) ? rows : []) {
      for (const value of [row && row.start, row && row.target]) {
        if (!realDay(value)) continue;
        if (value < first) first = value;
        if (value > last) last = value;
      }
    }
    const grown = (key: string) => {
      const value = reach && (reach as any)[key];
      return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
    };
    const lead = Math.max(PAD_DAYS, Math.ceil(LEAD_PX / mdTimelinePixelsPerDay(zoom, stretch)));
    let from = mdTimelineAddDays(first, -(lead + grown('behind')))!;
    /* The far end is whichever is further out: two years of planning room from
       today, or a week past the last date already set. A project targeted five
       years from now does not get its own end cut off. */
    const ahead = mdTimelineAddDays(today, AHEAD_DAYS + grown('ahead'))!;
    const past = mdTimelineAddDays(last, PAD_DAYS)!;
    let to = ahead > past ? ahead : past;
    const short = MIN_DAYS - (mdTimelineDayDiff(from, to) + 1);
    if (short > 0) {
      from = mdTimelineAddDays(from, -Math.ceil(short / 2))!;
      to = mdTimelineAddDays(to, Math.floor(short / 2))!;
    }
    const want = typeof minDays === 'number' && Number.isFinite(minDays) ? Math.floor(minDays) : 0;
    const room = want - (mdTimelineDayDiff(from, to) + 1);
    if (room > 0) to = mdTimelineAddDays(to, room)!;
    return { from, to, zoom: ZOOMS.indexOf(zoom as MdTimelineZoom) === -1 ? 'month' : zoom };
  }

  g.MD_TIMELINE_ZOOMS = ZOOMS;
  g.MD_TIMELINE_PPD = PPD;
  Object.assign(g, { mdTimelineAddDays, mdTimelineDayDiff, mdTimelineIsWeekStart,
    mdTimelineIsMonthStart, mdTimelinePixelsPerDay, mdTimelineAxis, mdTimelineX,
    mdTimelineDayAt, mdTimelineClampDay, mdTimelineMonths, mdTimelineTicks,
    mdTimelineToday, mdTimelineSpan, mdTimelineMove, mdTimelineResize, mdTimelineWindow,
    mdTimelineZoomPinch });
})(typeof window !== 'undefined' ? window : globalThis);
